import React, { useEffect, useMemo, useState } from 'react';
import { IListingData } from '@models/listing';
import StaticLayout from '@components/layout/StaticLayout';
import { useLazyQuery } from '@apollo/client';
import {
  getBPPlanListQuery,
  getAssignedPersonsQuery,
  getStaticLovsQuery,
  getLatestProposalList,
  getAssignedBusinessPartnersQuery,
} from './queries';
import {
  dataToLovs,
  extractAssignedBPWithPagination,
  getBPPlanLovs,
  mapToListingData,
  staticToLovs,
} from './utils';
import { filterSectionsContent, headers } from './content';
import { IListingFilterWidgetSection } from '@components/widgets/listing-filter-widget';
import ListingFilterWidget from '@components/widgets/listing-filter-widget/ListingFilterWidget';
import { getFilter, setFilter } from '@utils/filter-utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { toast } from 'react-toastify';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { useAppSelector } from '@redux/hooks';
import ProposalDrawer from '../proposal-drawer/ProposalDrawer';
import ListingWidget from '@components/widgets/listing-widget/ListingWidget';
import { IFilterModel } from '@components/widgets/listing-widget';
import { IEnhancedMenuItem } from '@components/table/types';
import { IAbstractRecord } from '@models';
import { isEqual } from 'lodash';
import { isEmpty } from '@utils/validationUtils';
import DateService from '@services/DateService';
import Loader from '@components/loader/Loader';

const ProposalsListingPage: React.FC = () => {
  const user = useAppSelector((state) => state.user);
  const businessUserId = user.info.businessUser_Id;
  const [proposalDrawerOpen, setProposalDrawerOpen] = useState<boolean>(false);
  const isBusinessUser = user.userRoles.includes('Core-BusinessUser');
  const isAdmin = user.userRoles.includes('Insurance-Admin');
  const FILTER_SESSION_KEY = 'proposalsFilter';

  const actions: IEnhancedMenuItem[] = useMemo(
    () => [
      ...[
        isBusinessUser || isAdmin
          ? {
              name: 'new',
              title: '+ New',
              onClick: () => {
                setProposalDrawerOpen(true);
              },
            }
          : undefined,
      ],
    ],
    [isBusinessUser, isAdmin]
  );

  const initialFilterValues =
    Object.keys(getFilter(FILTER_SESSION_KEY)).length > 0
      ? getFilter(FILTER_SESSION_KEY)
      : {
          line: [],
          subline: [],
          plan: [],
          businessPartner: [],
          insured: [],
          status: [],
          statusReason: [],
          effectiveOn: [],
        };

  const [filterValues, setFilterValues] = useState<IFilterModel>({
    namedFilters: initialFilterValues,
    pagination: {
      pageSize: 10,
      pageNumber: 1,
    },
  });

  const [filterSections, setFilterSections] =
    useState<IListingFilterWidgetSection[]>();
  const [lovs, setLovs] = useState<IAbstractRecord>({
    line: [],
    subline: [],
    plan: [],
    businessPartner: [],
    statusReason: [],
    status: [],
  });
  const [tableData, setTableData] = useState<IListingData>({
    pagedItems: {},
    pageSize: 10,
    pageNumber: 1,
    totalCount: 0,
  });

  const [getProposalListLazy, { loading: proposalListLoading }] = useLazyQuery(
    getLatestProposalList
  );
  const [getStaticLovs] = useLazyQuery(getStaticLovsQuery);
  const [getAssignedPersons] = useLazyQuery(getAssignedPersonsQuery);
  const [getAssignedBusinessPartnersLazy] = useLazyQuery(
    getAssignedBusinessPartnersQuery
  );
  const [getAssignedLinesAndPlansLazy, { loading: plansLoading }] =
    useLazyQuery(getBPPlanListQuery);

  useEffect(() => {
    initialize();
  }, []);

  const initialize = () => {
    Promise.all([
      getAssignedLinesAndPlansLazy({
        variables: {
          selectedBusinessUserID: businessUserId,
          selectedLineID: filterValues.namedFilters.line,
        },
      }),
      getStaticLovs(),
    ]).then(([{ data: assignedBPPlanList }, { data: staticLovsData }]) => {
      const planLovs = getBPPlanLovs(
        assignedBPPlanList,
        filterValues.namedFilters
      );
      const staticLovs = staticToLovs(staticLovsData);
      const lovs = {
        ...planLovs,
        ...staticLovs,
      };
      setLovs(lovs);
      setFilterSections(filterSectionsContent(lovs, filterValues.namedFilters));
    });
  };

  const getListingData = (filter = filterValues) => {
    if (!filter) {
      return {};
    }

    const filterV = filter.namedFilters;
    const pagination = filter.pagination;
    const formattedDateRange = DateService.formatDateRangeBackend(
      filterV.effectiveOn
    );
    return getProposalListLazy({
      variables: {
        pagination: pagination,
        KeywordSearch: isEmpty(filter.searchKey) ? null : filter.searchKey,
        OrderBy: {
          Attribute:
            filter.orderBy || 'Production_Proposal_PolicyEffectiveDate',
          Descending: filter.descending || false,
        },
        selectedLines:
          filterV?.line && filterV?.line?.length > 0 ? filterV?.line : null,
        selectedSublines:
          filterV?.subline && filterV?.subline?.length > 0
            ? filterV?.subline
            : null,
        selectedPlans:
          filterV?.plan && filterV?.plan?.length > 0 ? filterV?.plan : null,
        selectedBusinessPartners:
          filterV?.businessPartner && filterV?.businessPartner?.length > 0
            ? filterV?.businessPartner
            : null,
        selectedInsuredIDs:
          filterV?.insured && filterV?.insured?.length > 0
            ? filterV?.insured
            : null,
        SelectedStatuses:
          filterV?.status && filterV?.status?.length > 0
            ? filterV.status
            : null,
        selectedStatusReasons:
          filterV?.statusReason && filterV?.statusReason?.length > 0
            ? filterV.statusReason
            : null,
        FromEffectiveOn: formattedDateRange?.[0],
        ToEffectiveOn: formattedDateRange?.[1],
      },
    })
      .then(({ data: proposalsData }) => {
        if (proposalsData.error) {
          toast.error(
            <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
          );
          return;
        }

        const newTableData = mapToListingData(proposalsData);
        setTableData(newTableData);
      })
      .catch(() => {
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
      });
  };

  function handlePageChange(filterModel = filterValues): Promise<void> {
    return new Promise<void>(() => getListingData(filterModel));
  }

  const onFilterUpdate = async (
    filters: IAbstractRecord,
    inputName?: string
  ) => {
    if (isEqual(filters, filterValues.namedFilters)) {
      return;
    }
    const newFilters = {
      ...filterValues,
      namedFilters: filters,
    };

    if (inputName !== 'insured') {
      setFilter(filters, FILTER_SESSION_KEY);
    }
    setFilterValues(newFilters);
    getListingData(newFilters);

    if (inputName === 'line') {
      getAssignedLinesAndPlansLazy({
        variables: {
          selectedBusinessUserID: businessUserId,
          selectedLineID: newFilters.namedFilters.line,
        },
      }).then(({ data: assignedBPPlanList }) => {
        const planLovs = getBPPlanLovs(
          assignedBPPlanList,
          newFilters.namedFilters
        );
        const newLovs = {
          ...lovs,
          ...planLovs,
        };
        setLovs(newLovs);
        setFilterSections(
          filterSectionsContent(newLovs, newFilters.namedFilters)
        );
      });
    }
  };

  const renderMainChildren = () => (
    <>
      <ListingWidget
        title="Proposals"
        name="proposals"
        data={tableData}
        tableSettings={{ headers }}
        onPageChange={(filterModel) => {
          const newFilterModel = {
            ...filterValues,
            ...filterModel,
          };
          setFilterValues(newFilterModel);
          return handlePageChange(newFilterModel);
        }}
        usePagination
        disableSelection
        actions={actions}
        loading={proposalListLoading}
      />
      {proposalDrawerOpen && (
        <ProposalDrawer
          open={proposalDrawerOpen}
          onClose={() => setProposalDrawerOpen(false)}
          onSuccess={() => {
            getListingData();
          }}
        />
      )}
    </>
  );

  const renderFilter = () => (
    <ListingFilterWidget
      name=""
      disabled={plansLoading}
      filters={filterSections}
      loading={!filterSections}
      onApplyFilter={onFilterUpdate}
      onAutocompleteSearch={(fieldName: string, value: string, pagination) => {
        if (fieldName === 'businessPartner') {
          return getAssignedBusinessPartnersLazy({
            variables: {
              selectedBusinessUserID: businessUserId,
              searchKeyword: value,
              pagination,
            },
          }).then((result) => {
            return {
              options: extractAssignedBPWithPagination(result.data),
              totalCount:
                result.data.SalesforceManagement.queries.getAssignedBPList
                  .paging.totalCount,
            };
          });
        } else if (fieldName === 'insured') {
          return getAssignedPersons({
            variables: {
              selectedBusinessPartners:
                filterValues.namedFilters?.businessPartner,
              searchKeyword: value,
              pagination,
            },
          }).then((result) => {
            const lovs = dataToLovs(result.data, true);

            return {
              options: lovs.insured,
              totalCount:
                result.data?.Production?.queries
                  ?.GetAssignedPersonsWithPagination?.paging?.totalCount,
            };
          });
        }

        return new Promise<{
          options: Record<string, string>;
          totalCount: number;
        }>((resolve) => {
          resolve({ options: {}, totalCount: 0 });
        });
      }}
    />
  );

  return (
    <StaticLayout
      name="Proposals"
      leftChildren={renderFilter()}
      mainChildren={renderMainChildren()}
    />
  );
};

export default ProposalsListingPage;
